<template>
  <div class="android-waitlist" :class="{ 'rtl': isRTL }">
    <button class="lang-toggle" @click="toggleLanguage">
      {{ toggleLangLabel }}
    </button>
    <div class="content">
      <div class="logo">
        <img src="@/assets/logo.png" alt="Kewaar" class="logo-img" />
      </div>
      
      <h1 class="title">
        {{ t('android', 'title') }}<br>
        <span class="highlight">{{ t('android', 'subtitle') }}</span>
      </h1>
      
      <p class="description">
        {{ t('android', 'description') }}
      </p>
      
      <form @submit.prevent="handleSubmit" class="waitlist-form">
        <div class="input-group name-group">
          <input
            v-model="firstName"
            type="text"
            :placeholder="t('android', 'firstNamePlaceholder')"
            class="name-input"
            :disabled="isSubmitting"
          />
          <input
            v-model="lastName"
            type="text"
            :placeholder="t('android', 'lastNamePlaceholder')"
            class="name-input"
            :disabled="isSubmitting"
          />
        </div>
        <div class="input-group email-group">
          <input
            v-model="email"
            type="email"
            :placeholder="t('android', 'emailPlaceholder')"
            required
            class="email-input"
            :disabled="isSubmitting"
          />
          <button 
            type="submit" 
            class="submit-btn"
            :disabled="isSubmitting || !isValidEmail"
          >
            <span v-if="isSubmitting">{{ t('android', 'submitting') }}</span>
            <span v-else>{{ t('android', 'submitButton') }}</span>
          </button>
        </div>
        <p v-if="error" class="error-message">{{ error }}</p>
      </form>
      
      <div v-if="isSuccess" class="success-message">
        <div class="success-icon">✓</div>
        <p>{{ t('android', 'successMessage') }}</p>
      </div>
      
    </div>
  </div>
</template>

<script>
import { addToWaitlist } from '@/services/api'
import { i18nMixin } from '@/utils/i18n'

export default {
  name: 'AndroidWaitlist',
  mixins: [i18nMixin],
  data() {
    return {
      firstName: '',
      lastName: '',
      email: '',
      isSubmitting: false,
      isSuccess: false,
      error: null
    }
  },
  computed: {
    isValidEmail() {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return emailRegex.test(this.email)
    }
  },
  methods: {
    async handleSubmit() {
      if (!this.isValidEmail) {
        this.error = this.t('android', 'errorInvalidEmail')
        return
      }
      
      this.isSubmitting = true
      this.error = null
      
      try {
        const fullName = `${this.firstName} ${this.lastName}`.trim()
        await addToWaitlist({ name: fullName, email: this.email })
        
        this.isSuccess = true
        this.firstName = ''
        this.lastName = ''
        this.email = ''
      } catch (err) {
        console.log('Full error:', err)
        console.log('Error response:', err.response)
        console.log('Error response data:', err.response?.data)
        console.log('Error status:', err.response?.status)
        console.log('Error message:', err.message)
        
        if (err.message === 'Please wait a moment before submitting again') {
          this.error = err.message
        } else if (err.response && err.response.status === 422) {
          // Check if it's a duplicate or validation error
          const errorMessage = err.response.data?.message || ''
          const success = err.response.data?.success
          
          console.log('Success flag:', success)
          console.log('Error message from API:', errorMessage)
          
          // Only show success if API explicitly says already subscribed
          if (success === false && (errorMessage.toLowerCase().includes('already subscribed') || errorMessage.toLowerCase().includes('already registered'))) {
            this.isSuccess = true
          } else {
            this.error = errorMessage || 'Please check your information and try again.'
          }
        } else if (err.response && err.response.status === 429) {
          this.error = this.t('android', 'errorRateLimit')
        } else {
          this.error = this.t('android', 'errorGeneric')
          console.error('Waitlist error:', err)
        }
      } finally {
        this.isSubmitting = false
      }
    }
  }
}
</script>

<style scoped>
.android-waitlist {
  min-height: 100vh;
  background: #14171F;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  position: relative;
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #ffffff;
}

.android-waitlist.rtl {
  direction: rtl;
}

.android-waitlist.rtl .content {
  text-align: right;
}

.android-waitlist.rtl .name-group,
.android-waitlist.rtl .email-group {
  flex-direction: row-reverse;
}

.lang-toggle {
  position: absolute;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  padding: 8px 16px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.1);
  color: #ffffff;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s;
  z-index: 10;
}

.lang-toggle:hover {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.5);
}

.content {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  max-width: 500px;
  width: 100%;
  animation: fadeIn 0.8s ease;
}

.logo {
  margin-bottom: 32px;
}

.logo-img {
  width: 120px;
  height: auto;
  display: block;
}

.title {
  color: #ffffff;
  font-size: 3rem;
  font-weight: 300;
  line-height: 1.2;
  margin: 0 0 16px 0;
}

.highlight {
  color: #ffffff;
  font-weight: 500;
}

.description {
  color: #ffffff;
  font-size: 1.1rem;
  line-height: 1.6;
  margin: 0 0 32px 0;
  max-width: 400px;
}

.waitlist-form {
  width: 100%;
  margin-bottom: 24px;
}

.input-group {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: center;
  margin-bottom: 12px;
}

.name-group {
  display: flex;
  gap: 12px;
  width: 100%;
}

.name-input {
  flex: 1;
  min-width: 120px;
  padding: 16px 20px;
  border: 1px solid #3a3a4a;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.05);
  color: #fff;
  font-size: 1rem;
  transition: all 0.3s;
}

.name-input:focus {
  outline: none;
  border-color: #ffffff;
  background: rgba(255, 255, 255, 0.08);
}

.name-input::placeholder {
  color: #666;
}

.name-input:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.email-group {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: center;
  margin-bottom: 0;
}

.email-input {
  flex: 1;
  min-width: 250px;
  padding: 16px 20px;
  border: 1px solid #3a3a4a;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.05);
  color: #fff;
  font-size: 1rem;
  transition: all 0.3s;
}

.email-input:focus {
  outline: none;
  border-color: #ffffff;
  background: rgba(255, 255, 255, 0.08);
}

.email-input::placeholder {
  color: #666;
}

.email-input:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.submit-btn {
  padding: 16px 32px;
  border: none;
  border-radius: 12px;
  background: #ffffff;
  color: #14171F;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
  white-space: nowrap;
}

.submit-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(255, 255, 255, 0.3);
}

.submit-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.error-message {
  color: #e74c3c;
  font-size: 0.9rem;
  margin-top: 12px;
}

.success-message {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
  animation: fadeIn 0.5s ease;
}

.success-icon {
  width: 48px;
  height: 48px;
  background: #ffffff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  font-size: 1.5rem;
  color: #14171F;
}

.success-message p {
  color: #fff;
  margin: 0;
  font-size: 1rem;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

  @media (max-width: 768px) {
  .title {
    font-size: 2.5rem;
  }
  
  .description {
    font-size: 1rem;
  }
  
  .input-group {
    flex-direction: column;
  }
  
  .email-input {
    min-width: 100%;
  }
  
  .submit-btn {
    width: 100%;
  }
}

@media (max-width: 480px) {
  .title {
    font-size: 2rem;
  }
  
.logo-img {
  width: 100px;
}
}
</style>
